import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminMobileNav from '../../components/admin/AdminMobileNav';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  ArrowLeft, Download, RefreshCw, TrendingUp, TrendingDown, DollarSign,
  Users, Calendar, PieChart, BarChart3, LineChart
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart as RechartsLineChart, Line, PieChart as RechartsPieChart, Pie, 
  Cell, Legend, Area, AreaChart, ComposedChart
} from 'recharts';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#0f172a', '#f59e0b', '#3b82f6', '#10b981', '#8b5cf6', '#ef4444', '#ec4899', '#06b6d4'];

export default function AdminReports() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [revenueData, setRevenueData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [providerData, setProviderData] = useState([]);
  const [customerData, setCustomerData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [revenuePeriod, setRevenuePeriod] = useState('daily');
  const [dateRange, setDateRange] = useState('30');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [useCustomRange, setUseCustomRange] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [overviewRes, revenueRes, categoryRes, providerRes, customerRes] = await Promise.all([
        axios.get(`${API}/admin/reports/overview`),
        axios.get(`${API}/admin/reports/revenue?period=${revenuePeriod}&days=${dateRange}`),
        axios.get(`${API}/admin/reports/categories`),
        axios.get(`${API}/admin/reports/providers`),
        axios.get(`${API}/admin/reports/customers`)
      ]);
      
      setOverview(overviewRes.data);
      setRevenueData(revenueRes.data.data || []);
      setCategoryData(categoryRes.data || []);
      setProviderData(providerRes.data || []);
      setCustomerData(customerRes.data || []);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    } finally {
      setIsLoading(false);
    }
  }, [revenuePeriod, dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async (type) => {
    try {
      let url = `${API}/admin/reports/export?report_type=${type}&format=csv`;
      
      // Add date range for revenue export if custom range is selected
      if (type === 'revenue' && useCustomRange && customStartDate && customEndDate) {
        url += `&start_date=${customStartDate}&end_date=${customEndDate}`;
      }
      
      // All exports now download as CSV
      const response = await axios.get(url, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `${type}_report_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(downloadUrl);
      toast.success(`${type === 'full' ? 'Full' : type.charAt(0).toUpperCase() + type.slice(1)} report downloaded!`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Export failed');
    }
  };

  const formatCurrency = (value) => `Php ${value?.toLocaleString() || 0}`;

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <AdminSidebar />
      <AdminMobileNav />

      <main className="flex-1 p-4 md:p-8 md:ml-64 pt-16 md:pt-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/admin')}
              className="w-10 h-10 rounded-full hover:bg-stone-200 flex items-center justify-center md:hidden"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-[Poppins]">Reports & Analytics</h1>
              <p className="text-slate-500">Business performance insights</p>
            </div>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Button variant="outline" onClick={fetchData} data-testid="refresh-reports-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button 
              onClick={() => handleExport('full')}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900"
              data-testid="export-all-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export All
            </Button>
          </div>
        </div>

        {/* KPI Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <span className="flex items-center text-xs text-green-600">
                <TrendingUp className="w-3 h-3 mr-1" />
                +12%
              </span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{formatCurrency(overview?.revenue?.total)}</p>
            <p className="text-sm text-slate-500">Total Revenue</p>
          </div>
          
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-amber-600" />
              </div>
              <span className="flex items-center text-xs text-green-600">
                <TrendingUp className="w-3 h-3 mr-1" />
                +8%
              </span>
            </div>
            <p className="text-2xl font-bold text-amber-600">{formatCurrency(overview?.revenue?.commission)}</p>
            <p className="text-sm text-slate-500">Commission Earned</p>
          </div>
          
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-900">{overview?.bookings?.total || 0}</p>
            <p className="text-sm text-slate-500">Total Bookings</p>
            <p className="text-xs text-slate-400 mt-1">{overview?.bookings?.completion_rate || 0}% completed</p>
          </div>
          
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-900">{overview?.users?.total || 0}</p>
            <p className="text-sm text-slate-500">Total Users</p>
            <p className="text-xs text-slate-400 mt-1">
              {overview?.users?.customers || 0} customers, {overview?.users?.providers || 0} providers
            </p>
          </div>
        </div>

        {/* Main Reports Tabs */}
        <Tabs defaultValue="revenue" className="w-full">
          <TabsList className="mb-6 flex-wrap h-auto gap-1">
            <TabsTrigger value="revenue" data-testid="revenue-tab">
              <BarChart3 className="w-4 h-4 mr-2" />
              Revenue
            </TabsTrigger>
            <TabsTrigger value="bookings" data-testid="bookings-tab">
              <Calendar className="w-4 h-4 mr-2" />
              Bookings
            </TabsTrigger>
            <TabsTrigger value="categories" data-testid="categories-tab">
              <PieChart className="w-4 h-4 mr-2" />
              Categories
            </TabsTrigger>
            <TabsTrigger value="providers" data-testid="providers-tab">
              <Users className="w-4 h-4 mr-2" />
              Providers
            </TabsTrigger>
            <TabsTrigger value="customers" data-testid="customers-tab">
              <Users className="w-4 h-4 mr-2" />
              Customers
            </TabsTrigger>
          </TabsList>

          {/* Revenue Tab */}
          <TabsContent value="revenue">
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex flex-col gap-4 mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <h3 className="font-bold text-slate-900 font-[Poppins]">Revenue Trends</h3>
                  <div className="flex gap-3 flex-wrap">
                    <Select value={revenuePeriod} onValueChange={setRevenuePeriod}>
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="Group by" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" onClick={() => handleExport('revenue')} data-testid="export-revenue-btn">
                      <Download className="w-4 h-4 mr-2" />
                      Export CSV
                    </Button>
                  </div>
                </div>
                
                {/* Date Range Selection */}
                <div className="flex flex-wrap items-center gap-3 p-3 bg-slate-50 rounded-xl">
                  <span className="text-sm font-medium text-slate-600">Date Range:</span>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button 
                      variant={!useCustomRange && dateRange === '7' ? 'default' : 'outline'} 
                      size="sm"
                      onClick={() => { setUseCustomRange(false); setDateRange('7'); }}
                      className={!useCustomRange && dateRange === '7' ? 'bg-amber-500 hover:bg-amber-600 text-slate-900' : ''}
                    >
                      7 Days
                    </Button>
                    <Button 
                      variant={!useCustomRange && dateRange === '30' ? 'default' : 'outline'} 
                      size="sm"
                      onClick={() => { setUseCustomRange(false); setDateRange('30'); }}
                      className={!useCustomRange && dateRange === '30' ? 'bg-amber-500 hover:bg-amber-600 text-slate-900' : ''}
                    >
                      30 Days
                    </Button>
                    <Button 
                      variant={!useCustomRange && dateRange === '90' ? 'default' : 'outline'} 
                      size="sm"
                      onClick={() => { setUseCustomRange(false); setDateRange('90'); }}
                      className={!useCustomRange && dateRange === '90' ? 'bg-amber-500 hover:bg-amber-600 text-slate-900' : ''}
                    >
                      90 Days
                    </Button>
                    <Button 
                      variant={!useCustomRange && dateRange === '365' ? 'default' : 'outline'} 
                      size="sm"
                      onClick={() => { setUseCustomRange(false); setDateRange('365'); }}
                      className={!useCustomRange && dateRange === '365' ? 'bg-amber-500 hover:bg-amber-600 text-slate-900' : ''}
                    >
                      1 Year
                    </Button>
                    <span className="text-slate-400 mx-1">|</span>
                    <Button 
                      variant={useCustomRange ? 'default' : 'outline'} 
                      size="sm"
                      onClick={() => setUseCustomRange(true)}
                      className={useCustomRange ? 'bg-amber-500 hover:bg-amber-600 text-slate-900' : ''}
                    >
                      Custom
                    </Button>
                  </div>
                  
                  {useCustomRange && (
                    <div className="flex items-center gap-2 ml-2">
                      <Input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        className="w-36"
                        data-testid="custom-start-date"
                      />
                      <span className="text-slate-400">to</span>
                      <Input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        className="w-36"
                        data-testid="custom-end-date"
                      />
                      <Button 
                        size="sm" 
                        onClick={fetchData}
                        disabled={!customStartDate || !customEndDate}
                        data-testid="apply-date-range-btn"
                      >
                        Apply
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              
              {isLoading ? (
                <div className="h-[350px] flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
                </div>
              ) : revenueData.length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <ComposedChart data={revenueData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `Php ${v/1000}k`} />
                    <Tooltip 
                      formatter={(value) => [`Php ${value.toLocaleString()}`, '']}
                      contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                    />
                    <Legend />
                    <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#0f172a" fill="#0f172a" fillOpacity={0.05} />
                    <Bar dataKey="commission" name="Commission" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[350px] flex flex-col items-center justify-center text-slate-400">
                  <BarChart3 className="w-16 h-16 mb-4 opacity-50" />
                  <p>No revenue data available for this period</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="font-bold text-slate-900 font-[Poppins] mb-4">Booking Status Distribution</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsPieChart>
                    <Pie
                      data={[
                        { name: 'Completed', value: overview?.bookings?.completed || 0 },
                        { name: 'Active', value: overview?.bookings?.active || 0 },
                        { name: 'Pending', value: overview?.bookings?.pending || 0 },
                        { name: 'Cancelled', value: overview?.bookings?.cancelled || 0 },
                      ].filter(d => d.value > 0)}
                      dataKey="value"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {[0, 1, 2, 3].map((index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </div>
              
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="font-bold text-slate-900 font-[Poppins] mb-4">Booking Statistics</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-4 bg-green-50 rounded-xl">
                    <span className="text-green-700">Completed</span>
                    <span className="text-2xl font-bold text-green-700">{overview?.bookings?.completed || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-blue-50 rounded-xl">
                    <span className="text-blue-700">Active</span>
                    <span className="text-2xl font-bold text-blue-700">{overview?.bookings?.active || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-amber-50 rounded-xl">
                    <span className="text-amber-700">Pending</span>
                    <span className="text-2xl font-bold text-amber-700">{overview?.bookings?.pending || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-stone-50 rounded-xl">
                    <span className="text-stone-700">Cancelled</span>
                    <span className="text-2xl font-bold text-stone-700">{overview?.bookings?.cancelled || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories">
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-slate-900 font-[Poppins]">Revenue by Category</h3>
                  <Button variant="outline" size="sm" onClick={() => handleExport('categories')}>
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
                {categoryData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <RechartsPieChart>
                      <Pie
                        data={categoryData}
                        dataKey="revenue"
                        nameKey="category"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label={({ category, percent }) => `${category} ${(percent * 100).toFixed(0)}%`}
                      >
                        {categoryData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-slate-400">
                    No category data
                  </div>
                )}
              </div>
              
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="font-bold text-slate-900 font-[Poppins] mb-4">Category Performance</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-stone-100">
                        <th className="text-left py-3 text-slate-500 font-medium">Category</th>
                        <th className="text-right py-3 text-slate-500 font-medium">Bookings</th>
                        <th className="text-right py-3 text-slate-500 font-medium">Revenue</th>
                        <th className="text-right py-3 text-slate-500 font-medium">Rating</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categoryData.map((cat, idx) => (
                        <tr key={cat.category || `cat-${idx}`} className="border-b border-stone-50">
                          <td className="py-3 font-medium text-slate-900">{cat.category}</td>
                          <td className="py-3 text-right text-slate-600">{cat.bookings}</td>
                          <td className="py-3 text-right text-amber-600 font-medium">{formatCurrency(cat.revenue)}</td>
                          <td className="py-3 text-right">
                            <span className="text-amber-500">★</span> {cat.avg_rating?.toFixed(1) || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Providers Tab */}
          <TabsContent value="providers">
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-slate-900 font-[Poppins]">Provider Leaderboard</h3>
                <Button variant="outline" size="sm" onClick={() => handleExport('providers')}>
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-stone-200">
                      <th className="text-left py-3 text-slate-500 font-medium">Rank</th>
                      <th className="text-left py-3 text-slate-500 font-medium">Provider</th>
                      <th className="text-right py-3 text-slate-500 font-medium">Rating</th>
                      <th className="text-right py-3 text-slate-500 font-medium">Jobs</th>
                      <th className="text-right py-3 text-slate-500 font-medium">Completion</th>
                      <th className="text-right py-3 text-slate-500 font-medium">Earnings</th>
                      <th className="text-center py-3 text-slate-500 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {providerData.slice(0, 15).map((provider, idx) => (
                      <tr key={provider.id} className="border-b border-stone-50 hover:bg-stone-50">
                        <td className="py-3">
                          <span className={`w-7 h-7 rounded-full inline-flex items-center justify-center text-xs font-bold ${
                            idx === 0 ? 'bg-amber-100 text-amber-700' :
                            idx === 1 ? 'bg-slate-200 text-slate-700' :
                            idx === 2 ? 'bg-orange-100 text-orange-700' :
                            'bg-stone-100 text-stone-600'
                          }`}>
                            {idx + 1}
                          </span>
                        </td>
                        <td className="py-3">
                          <p className="font-medium text-slate-900">{provider.name}</p>
                          <p className="text-xs text-slate-400">{provider.email}</p>
                        </td>
                        <td className="py-3 text-right">
                          <span className="text-amber-500">★</span> {provider.rating?.toFixed(1) || '-'}
                        </td>
                        <td className="py-3 text-right text-slate-600">{provider.total_jobs || 0}</td>
                        <td className="py-3 text-right text-slate-600">{provider.completion_rate || 0}%</td>
                        <td className="py-3 text-right font-medium text-green-600">
                          {formatCurrency(provider.total_earnings)}
                        </td>
                        <td className="py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            provider.is_online ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                          }`}>
                            {provider.is_online ? 'Online' : 'Offline'}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {providerData.length === 0 && (
                      <tr>
                        <td colSpan="7" className="py-8 text-center text-slate-400">
                          No provider data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Customers Tab */}
          <TabsContent value="customers">
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-slate-900 font-[Poppins]">Top Customers</h3>
                <Button variant="outline" size="sm" onClick={() => handleExport('customers')}>
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-stone-200">
                      <th className="text-left py-3 text-slate-500 font-medium">Customer</th>
                      <th className="text-right py-3 text-slate-500 font-medium">Bookings</th>
                      <th className="text-right py-3 text-slate-500 font-medium">Completed</th>
                      <th className="text-right py-3 text-slate-500 font-medium">Total Spent</th>
                      <th className="text-right py-3 text-slate-500 font-medium">Wallet</th>
                      <th className="text-right py-3 text-slate-500 font-medium">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerData.slice(0, 15).map((customer) => (
                      <tr key={customer.id} className="border-b border-stone-50 hover:bg-stone-50">
                        <td className="py-3">
                          <p className="font-medium text-slate-900">{customer.name}</p>
                          <p className="text-xs text-slate-400">{customer.email}</p>
                        </td>
                        <td className="py-3 text-right text-slate-600">{customer.total_bookings || 0}</td>
                        <td className="py-3 text-right text-slate-600">{customer.completed_bookings || 0}</td>
                        <td className="py-3 text-right font-medium text-amber-600">
                          {formatCurrency(customer.total_spent)}
                        </td>
                        <td className="py-3 text-right text-slate-600">
                          {formatCurrency(customer.wallet_balance)}
                        </td>
                        <td className="py-3 text-right text-slate-400 text-xs">
                          {customer.joined ? new Date(customer.joined).toLocaleDateString() : '-'}
                        </td>
                      </tr>
                    ))}
                    {customerData.length === 0 && (
                      <tr>
                        <td colSpan="6" className="py-8 text-center text-slate-400">
                          No customer data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
